import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faChevronLeft,
  faChevronRight,
  faCommentDots,
  faEraser,
  faShieldHalved,
  faThumbsDown,
  faThumbsUp,
  faTimes,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import * as assistantService from 'services/assistant';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import AssistantCopyButton from './AssistantCopyButton';
import AssistantLoader from './AssistantLoader';

type Filters = assistantService.AssistantAdminFilters;
type SessionRow = assistantService.AssistantAdminSessionRow;
type SessionDetail = assistantService.AssistantAdminSessionDetail;

// "No limit" in the selects. SelectCustom compares option values with ===, so one sentinel of a number type is used
// for every select, as in the "log in as another user" dialog.
const ALL = -1;
const PAGE_SIZES = [25, 50, 100];
const TEXT_DEBOUNCE_MS = 400;
const GUARD_TOOL = 'guard';

interface ViewerFilters {
  company: string | number;
  department: number;
  branch: number;
  user: string | number;
  status: string | number;
  from: string;
  to: string;
  text: string;
  // Only sessions in which an answer was rated or commented on.
  hasFeedback: boolean;
}

const NO_FILTERS: ViewerFilters = {
  company: ALL,
  department: ALL,
  branch: ALL,
  user: ALL,
  status: ALL,
  from: '',
  to: '',
  text: '',
  hasFeedback: false,
};

const userKey = (companyId: string, userId: string) => `${companyId}:${userId}`;

/** The request the backend understands: only what is actually narrowed. */
const toQuery = (
  filters: ViewerFilters,
  page: number,
  pageSize: number,
): assistantService.AssistantAdminSessionQuery => {
  const query: assistantService.AssistantAdminSessionQuery = { page, pageSize };

  if (filters.company !== ALL) query.companyId = String(filters.company);
  if (filters.department !== ALL) query.departmentId = filters.department;
  if (filters.branch !== ALL) query.branchId = filters.branch;
  if (filters.user !== ALL) {
    const [companyId, userId] = String(filters.user).split(':');
    query.companyId = companyId;
    query.userId = userId;
  }
  if (filters.status !== ALL) query.status = String(filters.status);
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.text.trim()) query.text = filters.text.trim();
  if (filters.hasFeedback) query.hasFeedback = true;

  return query;
};

type RatingKind = 'up' | 'down' | 'note';

// 1 liked, -1 disliked, 0 a comment without a rating (the backend clamps a score to -5..5).
const ratingKind = (score: number): RatingKind => (score > 0 ? 'up' : score < 0 ? 'down' : 'note');

const RATING_ICON = { up: faThumbsUp, down: faThumbsDown, note: faCommentDots } as const;
const RATING_LABEL_KEY = {
  up: 'assistant.viewer.feedback_like',
  down: 'assistant.viewer.feedback_dislike',
  note: 'assistant.viewer.feedback_comment',
} as const;

const FEEDBACK_QUESTIONS = [
  { key: 'understood', labelKey: 'assistant.viewer.feedback_q_understood' },
  { key: 'correct', labelKey: 'assistant.viewer.feedback_q_correct' },
  { key: 'helped', labelKey: 'assistant.viewer.feedback_q_helped' },
] as const;

const answerLabel = (
  value: boolean | null | undefined,
  t: (key: string) => string,
): string => {
  if (value === true) {
    return t('assistant.viewer.feedback_yes');
  }
  if (value === false) {
    return t('assistant.viewer.feedback_no');
  }
  return t('assistant.viewer.feedback_unanswered');
};

const hasStructuredAnswers = (feedback: assistantService.AssistantAdminFeedback) =>
  feedback.understood != null || feedback.correct != null || feedback.helped != null;

const formatTokens = (value: number | undefined, language: string) => (value ?? 0).toLocaleString(language);

const formatDateTime = (iso: string, language: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const formatTime = (iso: string, language: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat(language, { timeStyle: 'medium' }).format(date);
};

const userLabel = (row: Pick<SessionRow, 'userName' | 'userId'>) => row.userName?.trim() || row.userId;

interface AssistantSessionViewerProps {
  onClose: () => void;
}

/**
 * The Session Viewer: every user's AI Assistant sessions in one wide dialog, filterable by company, department,
 * branch, user, status, dates and text, with a details dialog that shows a session's whole conversation. Only BePro
 * staff get the button that opens it, and the backend refuses everybody else, so this component takes no part in
 * deciding who may look.
 */
const AssistantSessionViewer: FC<AssistantSessionViewerProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [options, setOptions] = useState<Filters | null>(null);
  const [filters, setFilters] = useState<ViewerFilters>(NO_FILTERS);
  const [textDraft, setTextDraft] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [reloadToken, setReloadToken] = useState(0);

  const [data, setData] = useState<assistantService.AssistantAdminSessionList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  // The session whose feedback is open (the window that lists its rated answers in full).
  const [feedbackSessionId, setFeedbackSessionId] = useState<string | null>(null);

  // The lists the selects choose from: loaded once.
  useEffect(() => {
    let cancelled = false;
    assistantService
      .getAssistantAdminFilters()
      .then((result) => !cancelled && setOptions(result))
      .catch(() => !cancelled && setOptions({ companies: [], departments: [], branches: [], users: [], statuses: [] }));
    return () => {
      cancelled = true;
    };
  }, []);

  // The list. A newer request makes an older answer that arrives late irrelevant.
  const latestRequest = useRef(0);
  useEffect(() => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError(null);

    assistantService
      .listAssistantAdminSessions(toQuery(filters, page, pageSize))
      .then((result) => request === latestRequest.current && setData(result))
      .catch((err) => {
        if (request === latestRequest.current) {
          setError(err?.response?.status === 403 ? 'forbidden' : 'failed');
        }
      })
      .finally(() => request === latestRequest.current && setLoading(false));
  }, [filters, page, pageSize, reloadToken]);

  // Typing in the search box applies itself a moment after the last key; Enter applies at once.
  useEffect(() => {
    if (textDraft === filters.text) return;
    const timer = setTimeout(() => {
      setFilters((current) => ({ ...current, text: textDraft }));
      setPage(1);
    }, TEXT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [textDraft, filters.text]);

  // Escape closes the feedback window or the details first, then the viewer.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (feedbackSessionId) {
        setFeedbackSessionId(null);
      } else if (openSessionId) {
        setOpenSessionId(null);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [feedbackSessionId, openSessionId, onClose]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previous?.focus?.();
  }, []);

  const change = useCallback((patch: Partial<ViewerFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  }, []);

  // ---- select options. Department narrows branches and users, branch narrows users, company narrows users.
  const departmentOptions = useMemo(
    () => [
      { label: t('assistant.viewer.all_departments'), value: ALL },
      ...(options?.departments ?? []).map((d) => ({ label: `${d.name} | ${d.id}`, value: d.id })),
    ],
    [options, t],
  );

  const branchOptions = useMemo(
    () => [
      { label: t('assistant.viewer.all_branches'), value: ALL },
      ...(options?.branches ?? [])
        .filter((b) => filters.department === ALL || b.departmentId == null || b.departmentId === filters.department)
        .map((b) => ({ label: `${b.name} | ${b.id}`, value: b.id })),
    ],
    [options, filters.department, t],
  );

  const userOptions = useMemo(() => {
    const several = new Set((options?.users ?? []).map((u) => u.companyId)).size > 1;
    return [
      { label: t('assistant.viewer.all_users'), value: ALL },
      ...(options?.users ?? [])
        .filter(
          (u) =>
            (filters.company === ALL || u.companyId === filters.company) &&
            (filters.department === ALL || u.departmentId == null || u.departmentId === filters.department) &&
            (filters.branch === ALL || u.branchId == null || u.branchId === filters.branch),
        )
        .map((u) => ({
          label: `${u.name || u.userId} | ${u.userId}${several ? ` (${u.companyId})` : ''}`,
          value: userKey(u.companyId, u.userId),
        })),
    ];
  }, [options, filters.company, filters.department, filters.branch, t]);

  const companyOptions = useMemo(
    () => [
      { label: t('assistant.viewer.all_companies'), value: ALL },
      ...(options?.companies ?? []).map((c) => ({ label: c, value: c })),
    ],
    [options, t],
  );

  const statusOptions = useMemo(
    () => [
      { label: t('assistant.viewer.all_statuses'), value: ALL },
      ...(options?.statuses ?? []).map((s) => ({ label: s, value: s })),
    ],
    [options, t],
  );

  const total = data?.total ?? 0;
  const rows = data?.items ?? [];
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(total, (page - 1) * pageSize + rows.length);
  const hasNext = page * pageSize < total;
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS) || textDraft !== '';

  const reset = () => {
    setFilters(NO_FILTERS);
    setTextDraft('');
    setPage(1);
  };

  const onRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, sessionId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpenSessionId(sessionId);
    }
  };

  return createPortal(
    <div
      className="assistant-viewer-overlay"
      onMouseDown={(event) =>
        event.target === event.currentTarget && !openSessionId && !feedbackSessionId && onClose()
      }
    >
      <div
        ref={dialogRef}
        className="assistant-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assistant-viewer-title"
        tabIndex={-1}
      >
        <div className="assistant-viewer-head">
          <h5 id="assistant-viewer-title" className="assistant-viewer-title">
            {t('assistant.viewer.title')}
          </h5>
          <button
            type="button"
            className="assistant-viewer-icon-btn"
            onClick={onClose}
            title={t('assistant.viewer.close')}
            aria-label={t('assistant.viewer.close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="assistant-viewer-filters">
          <SelectCustom
            value={filters.company}
            onChange={(value: string | number) => change({ company: value, user: ALL })}
            options={companyOptions}
            movablePlaceholder={t('assistant.viewer.company')}
            appendToBody={true}
            menuPosition="fixed"
          />
          <SelectCustom
            value={filters.department}
            onChange={(value: number) => change({ department: value, branch: ALL, user: ALL })}
            options={departmentOptions}
            movablePlaceholder={t('assistant.viewer.department')}
            appendToBody={true}
            menuPosition="fixed"
          />
          <SelectCustom
            value={filters.branch}
            onChange={(value: number) => change({ branch: value, user: ALL })}
            options={branchOptions}
            movablePlaceholder={t('assistant.viewer.branch')}
            appendToBody={true}
            menuPosition="fixed"
          />
          <SelectCustom
            value={filters.user}
            onChange={(value: string | number) => change({ user: value })}
            options={userOptions}
            movablePlaceholder={t('assistant.viewer.user')}
            appendToBody={true}
            menuPosition="fixed"
          />
          <SelectCustom
            value={filters.status}
            onChange={(value: string | number) => change({ status: value })}
            options={statusOptions}
            movablePlaceholder={t('assistant.viewer.status')}
            appendToBody={true}
            menuPosition="fixed"
          />
          <label className="assistant-viewer-field">
            <span>{t('assistant.viewer.from')}</span>
            <input
              type="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(event) => change({ from: event.target.value })}
            />
          </label>
          <label className="assistant-viewer-field">
            <span>{t('assistant.viewer.to')}</span>
            <input
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(event) => change({ to: event.target.value })}
            />
          </label>
          <label className="assistant-viewer-field assistant-viewer-field--text">
            <span>{t('assistant.viewer.search')}</span>
            <input
              type="search"
              value={textDraft}
              placeholder={t('assistant.viewer.search_placeholder')}
              onChange={(event) => setTextDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  change({ text: textDraft });
                }
              }}
            />
          </label>
          <label className="assistant-viewer-check">
            <input
              type="checkbox"
              checked={filters.hasFeedback}
              onChange={(event) => change({ hasFeedback: event.target.checked })}
            />
            <FontAwesomeIcon icon={faCommentDots} />
            <span>{t('assistant.viewer.feedback_filter')}</span>
          </label>
          <div className="assistant-viewer-actions">
            <button type="button" className="assistant-viewer-btn" onClick={reset} disabled={!isFiltered}>
              <FontAwesomeIcon icon={faEraser} /> {t('assistant.viewer.reset')}
            </button>
            <button
              type="button"
              className="assistant-viewer-btn"
              onClick={() => setReloadToken((token) => token + 1)}
              title={t('assistant.viewer.refresh')}
            >
              <FontAwesomeIcon icon={faArrowsRotate} /> {t('assistant.viewer.refresh')}
            </button>
          </div>
        </div>

        <div className="assistant-viewer-body text-xsm" aria-busy={loading}>
          {error && (
            <div className="assistant-viewer-state assistant-viewer-state--error" role="alert">
              {t(error === 'forbidden' ? 'assistant.viewer.forbidden' : 'assistant.viewer.error')}
            </div>
          )}

          {!error && (
            <table className="assistant-viewer-table">
              <thead>
                <tr>
                  <th>{t('assistant.viewer.col_started')}</th>
                  <th>{t('assistant.viewer.col_last')}</th>
                  <th>{t('assistant.viewer.col_user')}</th>
                  <th>{t('assistant.viewer.col_company')}</th>
                  <th>{t('assistant.viewer.col_department')}</th>
                  <th>{t('assistant.viewer.col_branch')}</th>
                  <th className="num">{t('assistant.viewer.col_messages')}</th>
                  <th className="num" title={t('assistant.viewer.tokens_in_out_hint')}>
                    {t('assistant.viewer.col_tokens')}
                  </th>
                  <th>{t('assistant.viewer.col_feedback')}</th>
                  <th>{t('assistant.viewer.col_status')}</th>
                  <th>{t('assistant.viewer.col_first_message')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.sessionId}
                    tabIndex={0}
                    className={row.sessionId === openSessionId ? 'is-open' : undefined}
                    onClick={() => setOpenSessionId(row.sessionId)}
                    onKeyDown={(event) => onRowKeyDown(event, row.sessionId)}
                    title={row.sessionId}
                  >
                    <td className="nowrap">{formatDateTime(row.createdAt, i18n.language)}</td>
                    <td className="nowrap">{formatDateTime(row.lastActivityAt, i18n.language)}</td>
                    <td>
                      <div className="assistant-viewer-user">{userLabel(row)}</div>
                      {row.userName?.trim() && <div className="assistant-viewer-sub">{row.userId}</div>}
                    </td>
                    <td>{row.companyId}</td>
                    <td>{row.departmentName || row.departmentId || '—'}</td>
                    <td>{row.branchName || row.branchId || '—'}</td>
                    <td className="num">{row.messageCount}</td>
                    <td className="num nowrap" title={t('assistant.viewer.tokens_in_out_hint')}>
                      {row.promptTokens || row.completionTokens
                        ? `${formatTokens(row.promptTokens, i18n.language)} / ${formatTokens(row.completionTokens, i18n.language)}`
                        : '—'}
                    </td>
                    <td className="nowrap">
                      <FeedbackCell row={row} onOpen={() => setFeedbackSessionId(row.sessionId)} />
                    </td>
                    <td>
                      <span className={`assistant-viewer-status assistant-viewer-status--${row.status}`}>{row.status}</span>
                    </td>
                    <td className="assistant-viewer-first">{row.firstUserMessage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!error && !loading && rows.length === 0 && (
            <div className="assistant-viewer-state">{t('assistant.viewer.empty')}</div>
          )}

          {loading && (
            <div className="assistant-viewer-state assistant-viewer-state--loading">
              <AssistantLoader />
            </div>
          )}
        </div>

        <div className="assistant-viewer-foot">
          <label className="assistant-viewer-pagesize">
            <span>{t('assistant.viewer.rows_per_page')}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span className="assistant-viewer-range">
            {t('assistant.viewer.range', { from: firstRow, to: lastRow, total })}
          </span>
          <div className="assistant-viewer-pager">
            <button
              type="button"
              className="assistant-viewer-icon-btn"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              title={t('assistant.viewer.prev')}
              aria-label={t('assistant.viewer.prev')}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              type="button"
              className="assistant-viewer-icon-btn"
              disabled={!hasNext || loading}
              onClick={() => setPage((current) => current + 1)}
              title={t('assistant.viewer.next')}
              aria-label={t('assistant.viewer.next')}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>

      {openSessionId && (
        <AssistantSessionDetails sessionId={openSessionId} onClose={() => setOpenSessionId(null)} />
      )}

      {feedbackSessionId && (
        <AssistantFeedbackDialog
          sessionId={feedbackSessionId}
          onClose={() => setFeedbackSessionId(null)}
          onOpenSession={() => {
            setOpenSessionId(feedbackSessionId);
            setFeedbackSessionId(null);
          }}
        />
      )}
    </div>,
    document.body,
  );
};

// ======================================================================================================== feedback

/** The Feedback cell of a list row: how many answers were liked, disliked or only commented on. Click opens them. */
const FeedbackCell: FC<{ row: SessionRow; onOpen: () => void }> = ({ row, onOpen }) => {
  const { t } = useTranslation();
  const counts: [RatingKind, number][] = [
    ['up', row.feedbackUp ?? 0],
    ['down', row.feedbackDown ?? 0],
    ['note', row.feedbackNeutral ?? 0],
  ];
  const shown = counts.filter(([, count]) => count > 0);

  if (shown.length === 0) {
    return <>—</>;
  }

  return (
    <button
      type="button"
      className="assistant-viewer-feedback-cell"
      title={t('assistant.viewer.feedback_open')}
      // The row opens the session on click and on Enter / Space; this button opens the feedback instead.
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {shown.map(([kind, count]) => (
        <span key={kind} className={`assistant-viewer-rating is-${kind}`}>
          <FontAwesomeIcon icon={RATING_ICON[kind]} /> {count}
        </span>
      ))}
    </button>
  );
};

interface AssistantFeedbackDialogProps {
  sessionId: string;
  onClose: () => void;
  onOpenSession: () => void;
}

/**
 * The feedback of one session in full: for every answer a user rated or commented on, the rating, the whole comment,
 * who wrote it and when, the question that was asked and the answer that was given. Each card can be copied as plain
 * text to be forwarded to a developer.
 */
const AssistantFeedbackDialog: FC<AssistantFeedbackDialogProps> = ({ sessionId, onClose, onOpenSession }) => {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(false);
    assistantService
      .getAssistantAdminSession(sessionId)
      .then((result) => !cancelled && setDetail(result))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Each rated answer with the last thing the user said before it.
  const rated = useMemo(() => {
    const items: { message: assistantService.AssistantAdminMessage; question?: string }[] = [];
    let question: string | undefined;
    (detail?.messages ?? []).forEach((message) => {
      if (message.role === 'user') {
        question = message.text ?? undefined;
      } else if (message.role === 'assistant' && message.feedback) {
        items.push({ message, question });
      }
    });
    return items;
  }, [detail]);

  const session = detail?.session;

  const copyText = (message: assistantService.AssistantAdminMessage, question?: string) => {
    const feedback = message.feedback!;
    const name = feedback.userName?.trim();
    const who = name ? `${name} (${feedback.userId})` : feedback.userId;
    const answers = FEEDBACK_QUESTIONS.map(
      ({ key, labelKey }) => `${t(labelKey)}: ${answerLabel(feedback[key], t)}`,
    );
    return [
      t('assistant.viewer.feedback_copy_header'),
      `${t('assistant.session')}: ${sessionId}`,
      `${t('assistant.viewer.feedback_rating')}: ${t(RATING_LABEL_KEY[ratingKind(feedback.score)])}`,
      ...answers,
      `${t('assistant.viewer.col_user')}: ${who}${session ? `, ${t('assistant.viewer.col_company')} ${session.companyId}` : ''}`,
      `${t('assistant.viewer.feedback_date')}: ${formatDateTime(feedback.createdAt, i18n.language)}`,
      message.aiModel ? `${t('assistant.model')}: ${message.aiModel}` : '',
      `${t('assistant.viewer.feedback_comment_label')}: ${feedback.reason?.trim() || '—'}`,
      `${t('assistant.viewer.feedback_question')}: ${question?.trim() || '—'}`,
      `${t('assistant.viewer.feedback_answer')}: ${message.text?.trim() || '—'}`,
    ]
      .filter(Boolean)
      .join('\n');
  };

  return (
    <div
      className="assistant-viewer-details-overlay"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="assistant-viewer-details"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assistant-viewer-feedback-title"
      >
        <div className="assistant-viewer-head">
          <h5 id="assistant-viewer-feedback-title" className="assistant-viewer-title">
            {t('assistant.viewer.feedback_title')}
          </h5>
          <div className="assistant-viewer-session-id">
            <code title={sessionId}>{sessionId}</code>
            <AssistantCopyButton
              text={sessionId}
              label={t('assistant.copy_session_id')}
              copiedLabel={t('assistant.copied')}
            />
          </div>
          <button type="button" className="assistant-viewer-btn" onClick={onOpenSession}>
            {t('assistant.viewer.feedback_open_session')}
          </button>
          <button
            type="button"
            className="assistant-viewer-icon-btn"
            onClick={onClose}
            title={t('assistant.viewer.close')}
            aria-label={t('assistant.viewer.close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {!detail && !error && (
          <div className="assistant-viewer-state assistant-viewer-state--loading">
            <AssistantLoader />
          </div>
        )}

        {error && (
          <div className="assistant-viewer-state assistant-viewer-state--error" role="alert">
            {t('assistant.viewer.error')}
          </div>
        )}

        {detail && (
          <div className="assistant-viewer-fb-list text-xsm">
            {rated.length === 0 && <div className="assistant-viewer-state">{t('assistant.viewer.feedback_empty')}</div>}
            {rated.map(({ message, question }) => {
              const feedback = message.feedback!;
              const kind = ratingKind(feedback.score);
              return (
                <article key={message.messageId} className={`assistant-viewer-fb-card is-${kind}`}>
                  <header className="assistant-viewer-fb-head">
                    <span className={`assistant-viewer-rating is-${kind}`}>
                      <FontAwesomeIcon icon={RATING_ICON[kind]} /> {t(RATING_LABEL_KEY[kind])}
                    </span>
                    <span className="assistant-viewer-sub">
                      {feedback.userName?.trim() || feedback.userId} · {formatDateTime(feedback.createdAt, i18n.language)}
                    </span>
                    {message.aiModel && <span className="assistant-viewer-chip">{message.aiModel}</span>}
                    <span className="assistant-viewer-fb-copy">
                      <AssistantCopyButton
                        text={copyText(message, question)}
                        label={t('assistant.viewer.feedback_copy')}
                        copiedLabel={t('assistant.copied')}
                      />
                    </span>
                  </header>
                  {hasStructuredAnswers(feedback) && (
                    <ul className="assistant-viewer-fb-answers">
                      {FEEDBACK_QUESTIONS.map(({ key, labelKey }) => {
                        const value = feedback[key];
                        const answerClass =
                          value === true ? ' is-yes' : value === false ? ' is-no' : ' is-empty';
                        return (
                          <li key={key} className={'assistant-viewer-fb-answer' + answerClass}>
                            <span>{t(labelKey)}</span>
                            <strong>{answerLabel(value, t)}</strong>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="assistant-viewer-sub">{t('assistant.viewer.feedback_comment_label')}</div>
                  <div className="assistant-viewer-fb-comment" dir="auto">
                    {feedback.reason?.trim() || (
                      <span className="assistant-viewer-sub">{t('assistant.viewer.feedback_no_text')}</span>
                    )}
                  </div>
                  <div className="assistant-viewer-fb-pair">
                    <div>
                      <div className="assistant-viewer-sub">{t('assistant.viewer.feedback_question')}</div>
                      <div className="assistant-viewer-fb-text" dir="auto">
                        {question || '—'}
                      </div>
                    </div>
                    <div>
                      <div className="assistant-viewer-sub">{t('assistant.viewer.feedback_answer')}</div>
                      <div className="assistant-viewer-fb-text" dir="auto">
                        {message.text || '—'}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ======================================================================================================== details

type TimelineItem =
  | { kind: 'message'; at: number; order: number; message: assistantService.AssistantAdminMessage }
  | { kind: 'tool'; at: number; order: number; call: assistantService.AssistantAdminToolCall };

// Within the same instant the user speaks first, tools run, then the assistant answers.
const messageOrder = (role: string) => (role === 'user' ? 0 : 2);

const buildTimeline = (detail: SessionDetail, showTools: boolean): TimelineItem[] => {
  const items: TimelineItem[] = detail.messages.map((message) => ({
    kind: 'message',
    at: Date.parse(message.createdAt),
    order: messageOrder(message.role),
    message,
  }));

  if (showTools) {
    detail.toolCalls.forEach((call) => items.push({ kind: 'tool', at: Date.parse(call.createdAt), order: 1, call }));
  }

  return items.sort((a, b) => a.at - b.at || a.order - b.order);
};

const prettyInput = (input: unknown) => {
  if (input === null || input === undefined) return '';
  return typeof input === 'string' ? input : JSON.stringify(input, null, 2);
};

interface AssistantSessionDetailsProps {
  sessionId: string;
  onClose: () => void;
}

/** One session: who, when, and the whole conversation with the tool calls the assistant made along the way. */
const AssistantSessionDetails: FC<AssistantSessionDetailsProps> = ({ sessionId, onClose }) => {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [error, setError] = useState(false);
  const [showTools, setShowTools] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(false);
    assistantService
      .getAssistantAdminSession(sessionId)
      .then((result) => !cancelled && setDetail(result))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const timeline = useMemo(() => (detail ? buildTimeline(detail, showTools) : []), [detail, showTools]);
  const blocked = detail?.toolCalls.filter((c) => c.toolName === GUARD_TOOL && c.status === 'blocked').length ?? 0;
  const failed = detail?.toolCalls.filter((c) => c.toolName !== GUARD_TOOL && c.status !== 'success').length ?? 0;
  const session = detail?.session;

  return (
    <div
      className="assistant-viewer-details-overlay"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="assistant-viewer-details"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assistant-viewer-details-title"
      >
        <div className="assistant-viewer-head">
          <h5 id="assistant-viewer-details-title" className="assistant-viewer-title">
            {t('assistant.viewer.details')}
          </h5>
          <div className="assistant-viewer-session-id">
            <code title={sessionId}>{sessionId}</code>
            <AssistantCopyButton
              text={sessionId}
              label={t('assistant.copy_session_id')}
              copiedLabel={t('assistant.copied')}
            />
          </div>
          <button
            type="button"
            className="assistant-viewer-icon-btn"
            onClick={onClose}
            title={t('assistant.viewer.close')}
            aria-label={t('assistant.viewer.close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {!detail && !error && (
          <div className="assistant-viewer-state assistant-viewer-state--loading">
            <AssistantLoader />
          </div>
        )}

        {error && (
          <div className="assistant-viewer-state assistant-viewer-state--error" role="alert">
            {t('assistant.viewer.error')}
          </div>
        )}

        {detail && session && (
          <>
            <dl className="assistant-viewer-meta text-sm">
              <div>
                <dt>{t('assistant.viewer.col_user')}</dt>
                <dd>
                  {userLabel(session)}
                  {session.userName?.trim() && <span className="assistant-viewer-sub"> ({session.userId})</span>}
                </dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.col_company')}</dt>
                <dd>{session.companyId}</dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.col_department')}</dt>
                <dd>
                  {session.departmentName || '—'}
                  {session.departmentId ? <span className="assistant-viewer-sub"> ({session.departmentId})</span> : null}
                </dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.col_branch')}</dt>
                <dd>
                  {session.branchName || '—'}
                  {session.branchId ? <span className="assistant-viewer-sub"> ({session.branchId})</span> : null}
                </dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.col_status')}</dt>
                <dd>
                  <span className={`assistant-viewer-status assistant-viewer-status--${session.status}`}>
                    {session.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.locale')}</dt>
                <dd>{session.locale || '—'}</dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.col_started')}</dt>
                <dd>{formatDateTime(session.createdAt, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.col_last')}</dt>
                <dd>{formatDateTime(session.lastActivityAt, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.search_id')}</dt>
                <dd className="assistant-viewer-mono">{session.searchId || '—'}</dd>
              </div>
              <div>
                <dt>{t('assistant.viewer.tokens')}</dt>
                <dd>{detail.totalTokens.toLocaleString(i18n.language)}</dd>
              </div>
            </dl>

            <div className="assistant-viewer-convo-head">
              <h6>{t('assistant.viewer.conversation')}</h6>
              <span className="assistant-viewer-chip">
                {t('assistant.viewer.col_messages')}: {detail.messages.length}
              </span>
              <span className="assistant-viewer-chip">
                {t('assistant.viewer.tool_calls')}: {detail.toolCalls.length}
              </span>
              {blocked > 0 && (
                <span className="assistant-viewer-chip assistant-viewer-chip--warn">
                  {t('assistant.viewer.blocked_by_guard')}: {blocked}
                </span>
              )}
              {failed > 0 && (
                <span className="assistant-viewer-chip assistant-viewer-chip--bad">
                  {t('assistant.viewer.failed_calls')}: {failed}
                </span>
              )}
              <label className="assistant-viewer-toggle">
                <input type="checkbox" checked={showTools} onChange={(event) => setShowTools(event.target.checked)} />
                {t('assistant.viewer.show_tools')}
              </label>
            </div>

            <div className="assistant-viewer-timeline text-xsm">
              {timeline.length === 0 && <div className="assistant-viewer-state">{t('assistant.viewer.no_messages')}</div>}
              {timeline.map((item) =>
                item.kind === 'message' ? (
                  <TimelineMessage key={item.message.messageId} message={item.message} language={i18n.language} />
                ) : (
                  <TimelineToolCall key={item.call.toolLogId} call={item.call} language={i18n.language} />
                ),
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TimelineMessage: FC<{ message: assistantService.AssistantAdminMessage; language: string }> = ({
  message,
  language,
}) => {
  const { t } = useTranslation();
  const refused = message.role === 'assistant' && message.aiModel === GUARD_TOOL;
  const role = message.role === 'user' || message.role === 'assistant' ? message.role : 'system';

  return (
    <div className={`assistant-viewer-msg assistant-viewer-msg--${role}${refused ? ' is-refused' : ''}`}>
      <div className="assistant-viewer-msg-meta">
        <strong>{t(`assistant.viewer.role_${role}`)}</strong>
        <span>{formatTime(message.createdAt, language)}</span>
        {refused && (
          <span className="assistant-viewer-chip assistant-viewer-chip--warn">
            <FontAwesomeIcon icon={faShieldHalved} /> {t('assistant.viewer.blocked_by_guard')}
          </span>
        )}
        {!refused && message.aiModel && <span className="assistant-viewer-chip">{message.aiModel}</span>}
        {message.totalTokens ? (
          <span className="assistant-viewer-sub">
            {message.totalTokens.toLocaleString(language)} {t('assistant.viewer.tokens_short')}
            {message.promptTokens != null && message.completionTokens != null
              ? ` (${message.promptTokens} + ${message.completionTokens})`
              : ''}
          </span>
        ) : null}
        {message.costUsd ? <span className="assistant-viewer-sub">${message.costUsd}</span> : null}
      </div>
      <div className="assistant-viewer-msg-text">{message.text}</div>
      {message.feedback && <TimelineFeedback feedback={message.feedback} language={language} />}
    </div>
  );
};

/** What the user said about an answer, shown right under it in the conversation. */
const TimelineFeedback: FC<{ feedback: assistantService.AssistantAdminFeedback; language: string }> = ({
  feedback,
  language,
}) => {
  const { t } = useTranslation();
  const kind = ratingKind(feedback.score);

  return (
    <div className={`assistant-viewer-msg-feedback is-${kind}`}>
      <span className={`assistant-viewer-rating is-${kind}`}>
        <FontAwesomeIcon icon={RATING_ICON[kind]} /> {t(RATING_LABEL_KEY[kind])}
      </span>
      <span className="assistant-viewer-sub">
        {feedback.userName?.trim() || feedback.userId} · {formatDateTime(feedback.createdAt, language)}
      </span>
      {hasStructuredAnswers(feedback) && (
        <ul className="assistant-viewer-fb-answers">
          {FEEDBACK_QUESTIONS.map(({ key, labelKey }) => {
            const value = feedback[key];
            const answerClass = value === true ? ' is-yes' : value === false ? ' is-no' : ' is-empty';
            return (
              <li key={key} className={'assistant-viewer-fb-answer' + answerClass}>
                <span>{t(labelKey)}</span>
                <strong>{answerLabel(value, t)}</strong>
              </li>
            );
          })}
        </ul>
      )}
      {feedback.reason?.trim() && (
        <div className="assistant-viewer-msg-feedback-text" dir="auto">
          {feedback.reason}
        </div>
      )}
    </div>
  );
};

const TimelineToolCall: FC<{ call: assistantService.AssistantAdminToolCall; language: string }> = ({
  call,
  language,
}) => {
  const { t } = useTranslation();
  const isGuard = call.toolName === GUARD_TOOL;
  const input = prettyInput(call.input);
  const hasDetails = !!input || !!call.errorMessage;

  return (
    <details className={`assistant-viewer-tool assistant-viewer-tool--${call.status}`}>
      <summary>
        <FontAwesomeIcon icon={isGuard ? faShieldHalved : faWrench} />
        <span className="assistant-viewer-tool-name">{call.toolName}</span>
        <span className={`assistant-viewer-status assistant-viewer-status--${call.status}`}>{call.status}</span>
        {call.errorCode && <span className="assistant-viewer-sub">{call.errorCode}</span>}
        {call.durationMs != null && <span className="assistant-viewer-sub">{call.durationMs} ms</span>}
        <span className="assistant-viewer-sub assistant-viewer-tool-time">{formatTime(call.createdAt, language)}</span>
      </summary>
      {hasDetails && (
        <div className="assistant-viewer-tool-body">
          {input && (
            <>
              <div className="assistant-viewer-sub">{t('assistant.viewer.input')}</div>
              <pre>{input}</pre>
            </>
          )}
          {call.errorMessage && (
            <>
              <div className="assistant-viewer-sub">{t('assistant.viewer.error_detail')}</div>
              <pre>{call.errorMessage}</pre>
            </>
          )}
        </div>
      )}
    </details>
  );
};

export default withErrorBoundary(AssistantSessionViewer, 'AssistantSessionViewer');
